import { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, Map, TrendingUp, Wallet, Clock, User, CheckCircle2, Home as HomeIcon, MapPin, Compass, Calendar } from "lucide-react";
import { Slider } from "../components/ui/slider";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_BASE = "https://calendly.com/clarity-getzentry/30min";

const STEPS_BASE = [
  { id: 1, title: "What are you looking for?", icon: Map },
  { id: 2, title: "Your goal", icon: TrendingUp },
  { id: 3, title: "Specifics", icon: Compass },
  { id: 4, title: "Your budget", icon: Wallet },
  { id: 5, title: "Loan & timeline", icon: Clock },
  { id: 6, title: "Your details", icon: User },
];

const LOOKING_FOR = [
  { value: "holiday_home", label: "Holiday Home", desc: "Rental-ready property in Goa or Uttarakhand.", icon: HomeIcon },
  { value: "plot", label: "Plot / Land", desc: "Appreciation-driven plot in a growth corridor.", icon: MapPin },
  { value: "not_sure", label: "Not sure yet", desc: "I want to understand options first.", icon: Compass },
];

const HOLIDAY_OUTCOMES = [
  { value: "rental_income", label: "Pure rental income", desc: "Monthly cash flow via Airbnb / short-term rentals." },
  { value: "self_use", label: "Self-use", desc: "A second home for my family." },
  { value: "hybrid", label: "Hybrid", desc: "Self-use sometimes, rental otherwise." },
  { value: "capital_appreciation", label: "Capital appreciation", desc: "Asset value growth over the years." },
];

const PLOT_OUTCOMES = [
  { value: "capital_appreciation", label: "Capital appreciation", desc: "Hold and grow over 5–7 years." },
  { value: "long_term_hold", label: "Long-term hold", desc: "Park capital in appreciating land." },
  { value: "build_later", label: "Build later", desc: "Buy now, build a home or villa later." },
  { value: "resale_flip", label: "Resale flip", desc: "Buy in early phase, exit when corridor matures." },
];

const NOT_SURE_OUTCOMES = [
  { value: "rental_income", label: "I want monthly cash flow" },
  { value: "capital_appreciation", label: "I want long-term appreciation" },
  { value: "self_use", label: "I want a second home" },
  { value: "hybrid", label: "I want a mix" },
];

const HOLIDAY_LOCATIONS = ["Goa", "Uttarakhand", "Either / open"];
const HOLIDAY_MGMT = ["Self-managed", "Operator-managed", "Not sure yet"];
const PLOT_HOLDING = ["1–3 years", "3–5 years", "5–7 years", "7+ years"];
const PLOT_BUILD = ["Yes — eventually", "No — pure investment", "Not sure"];
const TIMELINES = [
  { value: "immediate", label: "Immediate" },
  { value: "3_6_months", label: "3–6 months" },
  { value: "6_12_months", label: "6–12 months" },
  { value: "exploring", label: "Just exploring" },
];

const formatInr = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(n % 10000000 === 0 ? 0 : 2)}Cr`;
  return `₹${(n / 100000).toFixed(0)}L`;
};

export default function ClarityCall() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [data, setData] = useState({
    looking_for: "",
    desired_outcome: "",
    preferred_location: "",
    management_pref: "",
    holding_period: "",
    build_intent: "",
    budget_min: 7500000,
    budget_max: 15000000,
    loan_required: "",
    timeline: "",
    name: "",
    email: "",
    phone: "",
    notes: "",
  });

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const outcomes = useMemo(() => {
    if (data.looking_for === "holiday_home") return HOLIDAY_OUTCOMES;
    if (data.looking_for === "plot") return PLOT_OUTCOMES;
    return NOT_SURE_OUTCOMES;
  }, [data.looking_for]);

  const canNext = () => {
    if (step === 1) return data.looking_for !== "";
    if (step === 2) return data.desired_outcome !== "";
    if (step === 3) {
      if (data.looking_for === "holiday_home") return data.preferred_location && data.management_pref;
      if (data.looking_for === "plot") return data.holding_period && data.build_intent;
      return true;
    }
    if (step === 4) return data.budget_min >= 7500000 && data.budget_max >= data.budget_min;
    if (step === 5) return data.loan_required !== "" && data.timeline !== "";
    if (step === 6) return data.name && data.email && data.phone.length >= 7;
    return false;
  };

  // Build Calendly URL with safe standard prefill only (name + email)
  // Custom answers (a1, a2) can break if the Calendly event doesn't define
  // matching custom questions. All questionnaire data is already saved in our
  // DB and emailed to clarity@getzentry.in.
  const calendlyUrl = useMemo(() => {
    const params = new URLSearchParams({
      hide_event_type_details: "1",
      hide_gdpr_banner: "1",
    });
    if (data.name) params.set("name", data.name);
    if (data.email) params.set("email", data.email);
    return `${CALENDLY_BASE}?${params.toString()}`;
  }, [data.name, data.email]);

  // Load Calendly script + init widget when entering the booking step
  // (Iframe embed — no JS widget, no third-party errors.)

  const submit = async () => {
    setSubmitting(true);
    try {
      const notesParts = [];
      if (data.preferred_location) notesParts.push(`Preferred location: ${data.preferred_location}`);
      if (data.management_pref) notesParts.push(`Management: ${data.management_pref}`);
      if (data.holding_period) notesParts.push(`Holding: ${data.holding_period}`);
      if (data.build_intent) notesParts.push(`Build later: ${data.build_intent}`);
      if (data.notes) notesParts.push(`Notes: ${data.notes}`);

      await axios.post(`${API}/clarity-call/book`, {
        looking_for: data.looking_for,
        desired_outcome: data.desired_outcome,
        budget_min: data.budget_min,
        budget_max: data.budget_max,
        loan_required: data.loan_required,
        timeline: data.timeline,
        name: data.name,
        email: data.email,
        phone: data.phone,
        notes: notesParts.join(" | "),
      });
      setSubmitted(true);
      toast.success("Great — pick a time below.");
    } catch (e) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ================= Post-submit: Calendly booking =================
  if (submitted) {
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14" data-testid="clarity-success">
        <div className="text-xs uppercase tracking-widest text-[#1D4ED8]">Last step</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 max-w-3xl leading-[1.15]">
          Pick a 45-minute slot that works{data.name ? `, ${data.name.split(" ")[0]}` : ""}.
        </h1>
        <p className="text-zinc-600 mt-4 max-w-2xl">
          Your context has been pre-filled into the booking. Confirm a time and we'll see you on the call.
        </p>

        <div className="mt-8 bg-white border border-zinc-200 rounded-3xl overflow-hidden" data-testid="calendly-widget">
          <iframe
            src={calendlyUrl}
            width="100%"
            height="760"
            frameBorder="0"
            title="Book a Clarity Call with Zentry"
            style={{ border: 0, display: "block" }}
          />
        </div>
        <p className="text-xs text-zinc-500 mt-3">
          Calendar not loading? <a href={calendlyUrl} target="_blank" rel="noreferrer" className="text-[#1D4ED8] underline">Open it in a new tab</a>.
        </p>
      </div>
    );
  }

  const Icon = STEPS_BASE[step - 1].icon;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16" data-testid="clarity-call-page">
      <div className="text-xs uppercase tracking-widest text-[#1D4ED8]">Book a Clarity Call</div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 max-w-3xl leading-[1.15]">
        No pressure. No follow-ups. Just a structured 45-minute conversation.
      </h1>
      <p className="text-zinc-600 mt-4 max-w-2xl">
        Answer a few quick questions so we can prepare — then you'll pick a time on the next screen.
      </p>

      <div className="mt-10 flex items-center gap-2 flex-wrap">
        {STEPS_BASE.map((s) => (
          <div key={s.id} className="flex items-center gap-2" data-testid={`step-indicator-${s.id}`}>
            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium border ${
              step > s.id ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" :
              step === s.id ? "bg-white text-[#1D4ED8] border-[#1D4ED8]" :
              "bg-white text-zinc-400 border-zinc-300"
            }`}>
              {step > s.id ? <Check className="h-4 w-4" /> : s.id}
            </div>
            {s.id < STEPS_BASE.length && <div className={`h-[1px] w-6 ${step > s.id ? "bg-[#1D4ED8]" : "bg-zinc-300"}`} />}
          </div>
        ))}
      </div>

      <div className="mt-10 bg-white border border-zinc-200 rounded-3xl p-8 md:p-12 animate-fade-in">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#EFF6FF] text-[#1D4ED8] flex items-center justify-center">
            <Icon className="h-5 w-5" />
          </div>
          <h2 className="font-display text-2xl sm:text-3xl">{STEPS_BASE[step - 1].title}</h2>
        </div>

        {step === 1 && (
          <div className="grid md:grid-cols-3 gap-4" data-testid="step1">
            {LOOKING_FOR.map((o) => {
              const Ic = o.icon;
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => { update("looking_for", o.value); update("desired_outcome", ""); }}
                  data-testid={`looking-${o.value}`}
                  className={`text-left p-6 rounded-2xl border-2 transition-all ${
                    data.looking_for === o.value ? "border-[#1D4ED8] bg-[#EFF6FF]" : "border-zinc-200 bg-white hover:border-zinc-400"
                  }`}
                >
                  <Ic className="h-5 w-5 text-[#1D4ED8]" />
                  <div className="font-display text-xl mt-3">{o.label}</div>
                  <p className="text-sm text-zinc-600 mt-2">{o.desc}</p>
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div className="grid md:grid-cols-2 gap-4" data-testid="step2">
            {outcomes.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => update("desired_outcome", o.value)}
                data-testid={`outcome-${o.value}`}
                className={`text-left p-6 rounded-2xl border-2 transition-all ${
                  data.desired_outcome === o.value ? "border-[#1D4ED8] bg-[#EFF6FF]" : "border-zinc-200 bg-white hover:border-zinc-400"
                }`}
              >
                <div className="font-display text-xl">{o.label}</div>
                {o.desc && <p className="text-sm text-zinc-600 mt-2">{o.desc}</p>}
              </button>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-10" data-testid="step3">
            {data.looking_for === "holiday_home" && (
              <>
                <div>
                  <Label className="text-sm text-zinc-700">Preferred location</Label>
                  <div className="mt-4 grid md:grid-cols-3 gap-3">
                    {HOLIDAY_LOCATIONS.map((loc) => (
                      <button key={loc} type="button" onClick={() => update("preferred_location", loc)} data-testid={`loc-${loc}`}
                        className={`p-4 rounded-xl border-2 text-sm ${data.preferred_location === loc ? "border-[#1D4ED8] bg-[#EFF6FF]" : "border-zinc-200"}`}>
                        {loc}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-zinc-700">Management preference</Label>
                  <div className="mt-4 grid md:grid-cols-3 gap-3">
                    {HOLIDAY_MGMT.map((m) => (
                      <button key={m} type="button" onClick={() => update("management_pref", m)} data-testid={`mgmt-${m}`}
                        className={`p-4 rounded-xl border-2 text-sm ${data.management_pref === m ? "border-[#1D4ED8] bg-[#EFF6FF]" : "border-zinc-200"}`}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {data.looking_for === "plot" && (
              <>
                <div>
                  <Label className="text-sm text-zinc-700">Expected holding period</Label>
                  <div className="mt-4 grid md:grid-cols-4 gap-3">
                    {PLOT_HOLDING.map((h) => (
                      <button key={h} type="button" onClick={() => update("holding_period", h)} data-testid={`hold-${h}`}
                        className={`p-4 rounded-xl border-2 text-sm ${data.holding_period === h ? "border-[#1D4ED8] bg-[#EFF6FF]" : "border-zinc-200"}`}>
                        {h}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-zinc-700">Do you plan to build on this plot later?</Label>
                  <div className="mt-4 grid md:grid-cols-3 gap-3">
                    {PLOT_BUILD.map((b) => (
                      <button key={b} type="button" onClick={() => update("build_intent", b)} data-testid={`build-${b}`}
                        className={`p-4 rounded-xl border-2 text-sm ${data.build_intent === b ? "border-[#1D4ED8] bg-[#EFF6FF]" : "border-zinc-200"}`}>
                        {b}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {data.looking_for === "not_sure" && (
              <div className="bg-[#EFF6FF] border border-[#1D4ED8]/20 rounded-2xl p-6">
                <div className="font-display text-xl">No worries — that's exactly what the Clarity Call is for.</div>
                <p className="text-sm text-zinc-700 mt-2">We'll figure out direction together. Continue to tell us about your budget.</p>
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div data-testid="step4">
            <p className="text-zinc-600 mb-8">
              Our guidance starts at ₹75 Lakhs and goes up to ₹5 Cr+. Select your comfortable range.
            </p>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">Minimum</div>
                <div className="font-display text-3xl text-[#1D4ED8]">{formatInr(data.budget_min)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Maximum</div>
                <div className="font-display text-3xl text-[#1D4ED8]">{data.budget_max >= 50000000 ? "₹5Cr+" : formatInr(data.budget_max)}</div>
              </div>
            </div>
            <Slider value={[data.budget_min, data.budget_max]} min={7500000} max={50000000} step={2500000}
              onValueChange={(v) => setData((d) => ({ ...d, budget_min: v[0], budget_max: v[1] }))} data-testid="budget-slider" />
            <div className="flex justify-between text-xs text-zinc-500 mt-3">
              <span>₹75L</span><span>₹5Cr+</span>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-10" data-testid="step5">
            <div>
              <Label className="text-sm text-zinc-700">Will you need a loan?</Label>
              <RadioGroup value={data.loan_required} onValueChange={(v) => update("loan_required", v)} className="mt-4 grid md:grid-cols-3 gap-3">
                {[{ v: "yes", l: "Yes, I will need a loan" }, { v: "no", l: "No, fully self-funded" }, { v: "maybe", l: "Maybe, still deciding" }].map((opt) => (
                  <label key={opt.v} className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer ${data.loan_required === opt.v ? "border-[#1D4ED8] bg-[#EFF6FF]" : "border-zinc-200"}`}>
                    <RadioGroupItem value={opt.v} data-testid={`loan-${opt.v}`} />
                    <span>{opt.l}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label className="text-sm text-zinc-700">Investment timeline</Label>
              <div className="mt-4 grid md:grid-cols-4 gap-3">
                {TIMELINES.map((t) => (
                  <button key={t.value} type="button" onClick={() => update("timeline", t.value)} data-testid={`timeline-${t.value}`}
                    className={`p-4 rounded-xl border-2 text-sm ${data.timeline === t.value ? "border-[#1D4ED8] bg-[#EFF6FF]" : "border-zinc-200"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 6 && (
          <div className="space-y-6 max-w-xl" data-testid="step6">
            <div>
              <Label htmlFor="name" className="text-sm text-zinc-700">Full name</Label>
              <Input id="name" value={data.name} onChange={(e) => update("name", e.target.value)} className="mt-2" placeholder="Your name" data-testid="input-name" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="text-sm text-zinc-700">Email</Label>
                <Input id="email" type="email" value={data.email} onChange={(e) => update("email", e.target.value)} className="mt-2" placeholder="you@email.com" data-testid="input-email" />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm text-zinc-700">Phone</Label>
                <Input id="phone" value={data.phone} onChange={(e) => update("phone", e.target.value)} className="mt-2" placeholder="+91..." data-testid="input-phone" />
              </div>
            </div>
            <div>
              <Label htmlFor="notes" className="text-sm text-zinc-700">Anything specific you'd like to discuss?</Label>
              <Textarea id="notes" value={data.notes} onChange={(e) => update("notes", e.target.value)} className="mt-2" rows={3} placeholder="Optional" data-testid="input-notes" />
            </div>
            <div className="bg-[#EFF6FF] border border-[#1D4ED8]/20 rounded-xl p-4 flex gap-3 items-start">
              <Calendar className="h-5 w-5 text-[#1D4ED8] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-zinc-700">On the next screen you'll pick a 45-minute time slot that works. We'll pre-fill your details into the calendar.</p>
            </div>
          </div>
        )}

        <div className="mt-10 flex items-center justify-between">
          <Button variant="ghost" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} className="gap-2" data-testid="btn-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          {step < STEPS_BASE.length ? (
            <button onClick={() => setStep((s) => s + 1)} disabled={!canNext()}
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed" data-testid="btn-next">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={!canNext() || submitting} className="btn-primary disabled:opacity-40" data-testid="btn-submit">
              {submitting ? "Submitting..." : "Continue to booking"} <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-8 text-xs text-zinc-500 max-w-xl">
        By submitting, you agree to be contacted at the email and phone provided. No spam. No broker calls.
      </div>

      <div className="mt-6">
        <Link to="/" className="text-sm text-zinc-500 hover:text-[#1D4ED8] link-underline">← Back to Home</Link>
      </div>
    </div>
  );
}
